import {ANIMATION_DEFAULTS} from './constants'

export type ScrollMetrics = {
  vh: number
  fadeStartY: number
  contentStart: number
  contentEnd: number
  contentHeight: number
  fadeZoneHeight: number
  totalAnimationZone: number
  scrollProgress: number
}

export type WordAnimationState = {
  opacity: number
  blur: number
}

/**
 * Calculate scroll-based metrics for animation
 */
export function calculateScrollMetrics(fadeRatio: number, firstRect: DOMRect, lastRect: DOMRect): ScrollMetrics {
  const vh = window.innerHeight || document.documentElement.clientHeight
  const fadeStartY = vh * (1 - fadeRatio)

  const contentStart = firstRect.top
  const contentEnd = lastRect.bottom
  const contentHeight = contentEnd - contentStart

  const fadeZoneHeight = vh * fadeRatio
  const totalAnimationZone = contentHeight + fadeZoneHeight

  const scrollProgress = Math.max(0, Math.min(1, (fadeStartY - contentStart) / totalAnimationZone))

  return {
    vh,
    fadeStartY,
    contentStart,
    contentEnd,
    contentHeight,
    fadeZoneHeight,
    totalAnimationZone,
    scrollProgress,
  }
}

/**
 * Calculate word animation state based on position and progress
 */
export function calculateWordAnimationState(
  globalWordIndex: number,
  currentWordIndex: number,
  wordProgress: number,
  minOpacity: number,
  shouldAnimateParagraph: boolean,
): WordAnimationState {
  let opacity = minOpacity
  let blur = ANIMATION_DEFAULTS.BLUR_MAX

  if (!shouldAnimateParagraph) {
    opacity = minOpacity
    blur = ANIMATION_DEFAULTS.BLUR_MAX
  } else if (globalWordIndex < currentWordIndex) {
    opacity = 1
    blur = ANIMATION_DEFAULTS.BLUR_MIN
  } else if (globalWordIndex === currentWordIndex) {
    const eased = wordProgress * wordProgress * (3 - 2 * wordProgress)
    opacity = Math.max(minOpacity, eased)
    blur = ANIMATION_DEFAULTS.BLUR_MAX * (1 - eased)
  } else {
    opacity = minOpacity
    blur = ANIMATION_DEFAULTS.BLUR_MAX
  }

  return {opacity, blur}
}

/**
 * Check if paragraph animation is complete
 */
export function isParagraphComplete(
  currentWordIndex: number,
  paragraphEndWordIndex: number,
  wordProgress: number,
): boolean {
  return (
    currentWordIndex >= paragraphEndWordIndex &&
    (currentWordIndex > paragraphEndWordIndex || wordProgress >= ANIMATION_DEFAULTS.SMOOTHSTEP_THRESHOLD)
  )
}
